import { useEffect, useRef } from 'react';
import { RefreshCw, X } from 'lucide-react';
import './ServerInfoModal.css';

function age(seconds = 0) {
  let value = Math.max(0, Number(seconds || 0));
  const days = Math.floor(value / 86400); value %= 86400;
  const hours = Math.floor(value / 3600); value %= 3600;
  const minutes = Math.floor(value / 60);
  return [days ? `${days}d` : '', hours ? `${hours}h` : '', `${minutes}m`].filter(Boolean).join(' ');
}
function healthLabel(value) { return value ? 'HEALTHY' : 'DEGRADED'; }

export function ServerInfoModal({ open, info, clientVersion, busy, onRefresh, onClose, embedded = false }) {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!open || embedded) return undefined;
    previousFocusRef.current = document.activeElement;
    const focusable = () => [...(modalRef.current?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || [])]
      .filter((element) => !element.disabled && element.getAttribute('aria-hidden') !== 'true');
    requestAnimationFrame(() => focusable()[0]?.focus());
    const keydown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose?.(); return; }
      if (event.key !== 'Tab') return;
      const items = focusable(); if (!items.length) return;
      const first = items[0]; const last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      previousFocusRef.current?.focus?.();
    };
  }, [embedded, open, onClose]);

  if (!open) return null;
  const pack = info?.active_event_pack || {};
  const stats = info?.stats || {};
  const health = info?.health || {};
  const status = String(info?.status || 'unknown').toUpperCase();
  const content = <section ref={modalRef} className={`server-info-modal ${embedded ? 'embedded' : ''}`} role={embedded ? undefined : 'dialog'} aria-modal={embedded ? undefined : 'true'} aria-labelledby="server-info-title" onClick={(e) => e.stopPropagation()}>
      <header><div><span>THE DOPE MAN // SERVER</span><h2 id="server-info-title">System Status</h2></div>{!embedded && <button onClick={onClose} aria-label="Close server information"><X /></button>}</header>
      <div className={`server-overall ${status.toLowerCase()}`}><i></i><strong>{status}</strong><span>{info?.product || 'The Dope Man'}</span></div>
      <div className="server-info-grid">
        <article><span>Hub</span><strong>{info?.hub_version || '—'}</strong></article>
        <article><span>Web Client</span><strong>{clientVersion || '—'}</strong></article>
        <article><span>API</span><strong>Generation {info?.api_generation ?? '—'}</strong></article>
        <article><span>Uptime</span><strong>{age(info?.uptime_seconds)}</strong></article>
        <article><span>Game Mode</span><strong>{info?.default_game_mode || '—'}</strong></article>
        <article><span>Event Pack</span><strong>{pack.id || '—'}{pack.version ? ` · v${pack.version}` : ''}</strong></article>
        <article><span>Players</span><strong>{stats.active_sessions ?? 0} online / {stats.registered_players ?? 0}</strong></article>
        <article><span>Active Runs</span><strong>{stats.active_runs ?? 0}</strong></article>
      </div>
      <section className="server-health-list"><h3>Services</h3>{Object.entries(health).map(([key,value]) => <div key={key}><span>{key.replaceAll('_',' ')}</span><strong className={value ? 'ok' : 'warn'}>{healthLabel(value)}</strong></div>)}</section>
      <footer><small>{info?.release_id || ''}{info?.server_time ? ` · ${new Date(info.server_time).toLocaleString()}` : ''}</small><button disabled={busy} onClick={onRefresh}><RefreshCw size={16} /> {busy ? 'CHECKING…' : 'REFRESH'}</button></footer>
    </section>;
  if (embedded) return content;
  return <div className="server-info-backdrop" onClick={onClose}>{content}</div>;
}
